import AddIcon from '@mui/icons-material/Add';
import { Alert, AlertColor, Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import PageLoader from 'components/PageLoader';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import { MouseEvent, RefObject, useMemo, useRef, useState } from 'react';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { URLS } from '../../../../../utils/constants/urls';
import { fetchFromStorage } from '../../../../../utils/storage';
import { removeErrorFieldsFromValues } from '../../../../../utils/validators';
import { getDrawerWidth } from '../../../../../utils/validators/HelperFunctions';
import { AddReceivableFormModel, calculateReceivableDueDate } from './AddReceivableForm';
import Currency from '../../../../../utils/hooks/getCurrency';

type CorporateBookingContext = {
  salesAccountId?: number | null;
  salesBranchId?: number | null;
  contactId?: number | null;
  companyName?: string;
  branchName?: string;
  contactName?: string;
  isCorporateBooking?: boolean;
};

type AddReceivableButtonProps = {
  bookingId: number;
  onSuccess: () => void;
  disabled?: boolean;
  maxReceivableAmount?: number;
  defaultCompanyName?: string;
  defaultGstNumber?: string;
  corporateBooking?: CorporateBookingContext;
};

export const AddReceivableButton = ({
  bookingId,
  onSuccess,
  disabled = false,
  maxReceivableAmount = 0,
  defaultCompanyName = '',
  defaultGstNumber = '',
  corporateBooking,
}: AddReceivableButtonProps) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const isSalesManagementEnabled = fetchFromStorage('isSalesManagementEnabled') === 'true';
  const salesManagementCorporateMode =
    isSalesManagementEnabled && Boolean(corporateBooking?.isCorporateBooking);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const savingRef = useRef(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [message, setMessage] = useState<{ display: boolean; severity: any; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formRef: RefObject<Form | null | undefined> = useRef();

  const formModel = useMemo(
    () => AddReceivableFormModel(strings, { salesManagementCorporateMode }),
    [salesManagementCorporateMode],
  );

  const resetForm = () => {
    setHasError(false);
    setFormValues({});
    formRef?.current?.resetForm();
  };

  const handleClose = () => {
    resetForm();
    setOpenDrawer(false);
  };

  const handleOpenDrawer = () => {
    const trimmedCompanyName = (corporateBooking?.companyName || defaultCompanyName)?.trim();
    const trimmedGstNumber = defaultGstNumber?.trim();
    const initial: Record<string, unknown> = {};

    if (salesManagementCorporateMode) {
      if (trimmedCompanyName) {
        initial.companyDisplay = trimmedCompanyName;
        initial.companyName = trimmedCompanyName;
      }
      if (corporateBooking?.branchName) {
        initial.branchDisplay = corporateBooking.branchName;
      }
      if (corporateBooking?.contactName) {
        initial.contactDisplay = corporateBooking.contactName;
      }
    } else if (trimmedCompanyName) {
      initial.companyName = trimmedCompanyName;
    }

    if (trimmedGstNumber) {
      initial.gstNumber = trimmedGstNumber;
    }

    setFormValues(initial);
    setOpenDrawer(true);
  };

  const handleSaveHelper = async (data: any) => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    const requestBody = new FormData();
    requestBody.append('type', data.type);
    requestBody.append('amount', data.amount);
    requestBody.append('companyName', data.companyName || corporateBooking?.companyName || '');
    requestBody.append('creditDays', data.creditDays);

    if (salesManagementCorporateMode) {
      if (corporateBooking?.salesAccountId) {
        requestBody.append('salesAccountId', String(corporateBooking.salesAccountId));
      }
      if (corporateBooking?.salesBranchId) {
        requestBody.append('salesBranchId', String(corporateBooking.salesBranchId));
      }
      if (corporateBooking?.contactId) {
        requestBody.append('contactId', String(corporateBooking.contactId));
      }
    }

    if (data.gstNumber) {
      requestBody.append('gstNumber', data.gstNumber);
    }
    if (data.remarks) {
      requestBody.append('remarks', data.remarks);
    }
    for (let i = 0; i < data?.attachment?.length; i++) {
      requestBody.append('attachment', data.attachment[i]);
    }

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    setIsLoading(true);
    try {
      const { status } = await axiosInstance.post(URLS.addSettlementEntry(bookingId), requestBody, config);
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
        onSuccess();
        handleClose();
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.something_went_wrong,
        });
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error.response?.data?.error_description ||
          error.response?.data?.message ||
          strings.something_went_wrong,
      });
    } finally {
      savingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData) as any;

    if (salesManagementCorporateMode && !data.companyName) {
      data.companyName = corporateBooking?.companyName || '';
    }

    setHasError(false);
    if (isFormValid) {
      if (data.amount === '0' || parseFloat(data.amount) <= 0) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.amountGreaterThanZero,
        });
        return;
      }
      const amountNum = parseFloat(data.amount);
      if (amountNum > maxReceivableAmount) {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.receivable_exceedsOutstanding,
        });
        return;
      }
      handleSaveHelper(data);
    } else {
      setHasError(true);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleFormChange = (field: string, _value: unknown, formData: Record<string, unknown>) => {
    if (field === 'creditDays') {
      const dueDate = calculateReceivableDueDate(formData.creditDays as string | number);
      setFormValues({
        ...formData,
        dueDate,
      });
    }
  };

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={handleOpenDrawer}>
        {strings.receivable_button_add}
      </Button>

      <DrawerMenu
        open={openDrawer}
        handleClose={handleClose}
        closeButton
        title={strings.receivable_drawer_title}
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info" sx={{ alignItems: 'center' }}>
            <Typography variant="body2">
              {strings.receivable_maxOutstanding_hint}:{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {Currency(maxReceivableAmount)}
              </Box>
            </Typography>
          </Alert>
          <Grid container spacing={2}>
            <Form
              onChange={handleFormChange}
              hasError={hasError}
              ref={formRef as RefObject<Form>}
              model={formModel}
              values={formValues}
            />
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
              {strings.BookingDetailButonTextCancel}
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
              {strings.common_button_save}
            </Button>
          </Box>
        </Box>
      </DrawerMenu>
    </>
  );
};
